import { GenericStructValue, GenericCallCard, GenericEventCard } from './chunk-DUSA5N3T.js';
import { SubCard } from './chunk-ES722JR5.js';
import { COWSWAP, COWSWAP_COMPONENTS, COWSWAP_EVENTS, COWSWAP_CALLS, COWSWAP_KINDS, isRecord, ProtocolValue, humanizeFieldName, FactGrid } from './chunk-PPWVGY7F.js';
import { FileJson, ExternalLink } from 'lucide-react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { CopyButton } from '@authe/ui-core';
import { isEnriched, rawOf, displayOf } from '@authe/fe-commons/storage';
import { getFactLinkUrl, getFactLink, shortenHash } from '@authe/fe-commons/chains';

var PROVENANCE = /* @__PURE__ */ new Set(["digest", "source", "sourceUrl", "source_url"]);
function CowAppData({ decoded, display, raw, hint }) {
  const doc = isRecord(decoded) ? decoded : void 0;
  if (!doc) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  const named = hint?.fields ?? [];
  const keys = [...named.filter((n) => n in doc), ...Object.keys(doc).filter((n) => !named.includes(n))];
  const facts = keys.filter((name) => !PROVENANCE.has(name)).map((name) => ({
    label: humanizeFieldName(name),
    value: /* @__PURE__ */ jsx(ProtocolValue, { value: doc[name] }),
    when: doc[name]
  }));
  const digest = doc.digest ?? raw;
  return /* @__PURE__ */ jsxs(
    SubCard,
    {
      caption: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx(FileJson, { className: "size-3 shrink-0", "aria-hidden": true }),
        hint?.label ?? "App data"
      ] }),
      meta: /* @__PURE__ */ jsx(
        "span",
        {
          className: "text-2xs uppercase tracking-wide text-muted-foreground",
          title: "Declared off-chain by the order's app-data document; the transaction proves only the digest.",
          children: "off-chain"
        }
      ),
      children: [
        facts.length > 0 ? /* @__PURE__ */ jsx(FactGrid, { facts }) : null,
        digest != null && /* @__PURE__ */ jsx("div", { className: "mt-1.5 truncate font-mono text-2xs text-muted-foreground", title: String(digest), children: /* @__PURE__ */ jsx(ProtocolValue, { value: digest }) })
      ]
    }
  );
}
var ORDER_UID_FACT = "order_uid";
var ORDER_UID_RE = /^0x[0-9a-fA-F]{112}$/;
function cowOrderUid(value2) {
  const v = isEnriched(value2) ? rawOf(value2) ?? displayOf(value2) : value2;
  if (typeof v !== "string") return void 0;
  const t = v.trim();
  return ORDER_UID_RE.test(t) ? t : void 0;
}
function cowOrderUrl(value2) {
  const uid = cowOrderUid(value2);
  return uid ? getFactLinkUrl(COWSWAP.protocolId, ORDER_UID_FACT, uid) : null;
}
function cowExplorerName() {
  return getFactLink(COWSWAP.protocolId, ORDER_UID_FACT)?.explorer ?? "CoW Explorer";
}
function CowOrderUid({ raw, display, decoded }) {
  const value2 = raw ?? display ?? decoded;
  const uid = cowOrderUid(value2);
  const url = uid ? cowOrderUrl(value2) : null;
  const text = typeof value2 === "string" ? value2 : value2 == null ? "" : String(value2);
  if (!uid || !url) {
    return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 items-center gap-1 align-middle font-mono text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "break-all", title: text || void 0, children: uid ? shortenHash(uid) : text }),
      text && /* @__PURE__ */ jsx(CopyButton, { value: text })
    ] });
  }
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 items-center gap-1 align-middle", children: [
    /* @__PURE__ */ jsxs(
      "a",
      {
        href: url,
        target: "_blank",
        rel: "noopener noreferrer",
        title: `${uid} \u2014 view on ${cowExplorerName()}`,
        className: "inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline",
        children: [
          /* @__PURE__ */ jsx("span", { className: "break-all", children: shortenHash(uid) }),
          /* @__PURE__ */ jsx(ExternalLink, { className: "size-3 shrink-0", "aria-hidden": true })
        ]
      }
    ),
    /* @__PURE__ */ jsx(CopyButton, { value: uid })
  ] });
}
var CHIP = "inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-2xs";
function CowTradeFlags({
  decoded,
  display,
  raw,
  hint
}) {
  const flags = isRecord(decoded) ? decoded : void 0;
  if (!flags) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  const named = hint?.fields ?? [];
  const order = [...named.filter((n) => n in flags), ...Object.keys(flags).filter((n) => !named.includes(n))];
  const chips = order.filter((name) => flags[name] !== false && flags[name] != null).map((name) => /* @__PURE__ */ jsxs("span", { className: CHIP, title: humanizeFieldName(name), children: [
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: humanizeFieldName(name) }),
    flags[name] !== true && /* @__PURE__ */ jsx("span", { className: "font-medium", children: /* @__PURE__ */ jsx(ProtocolValue, { value: flags[name] }) })
  ] }, name));
  if (chips.length === 0) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  return /* @__PURE__ */ jsx("span", { className: "inline-flex flex-wrap items-center gap-1 align-middle", children: chips });
}

// src/protocols/cowswap/index.ts
var PID = COWSWAP.protocolId;
var C = COWSWAP_COMPONENTS;
var value = (kind, component) => ({
  protocolId: PID,
  kind,
  component,
  fields: []
});
var call = (name, component) => ({
  protocolId: PID,
  call: name,
  component,
  summaryFields: []
});
var event = (name, component) => ({
  protocolId: PID,
  event: name,
  component,
  summaryFields: []
});
var COWSWAP_HINTS = {
  valueHints: [
    value(COWSWAP_KINDS.orderUid, C.orderUid),
    value(COWSWAP_KINDS.tradeFlags, C.tradeFlags),
    value(COWSWAP_KINDS.order, C.order),
    value(COWSWAP_KINDS.appData, C.appData)
  ],
  callHints: [
    call(COWSWAP_CALLS.settle, C.settle),
    call(COWSWAP_CALLS.createOrder, C.createOrder),
    call(COWSWAP_CALLS.invalidateOrders, C.invalidateOrders)
  ],
  eventHints: [
    event(COWSWAP_EVENTS.trade, C.trade),
    event(COWSWAP_EVENTS.settlement, C.settlement),
    event(COWSWAP_EVENTS.interaction, C.interactionLog),
    event(COWSWAP_EVENTS.orderPlacement, C.orderPlacement),
    event(COWSWAP_EVENTS.orderRefund, C.orderRefund)
  ]
};
function registerCowSwap(registry) {
  registry.registerValue(C.orderUid, CowOrderUid);
  registry.registerValue(C.tradeFlags, CowTradeFlags);
  registry.registerValue(C.appData, CowAppData);
  registry.registerValue(C.order, GenericStructValue);
  registry.registerCall(C.settle, GenericCallCard);
  registry.registerCall(C.createOrder, GenericCallCard);
  registry.registerCall(C.invalidateOrders, GenericCallCard);
  registry.registerEvent(C.trade, GenericEventCard);
  registry.registerEvent(C.settlement, GenericEventCard);
  registry.registerEvent(C.interactionLog, GenericEventCard);
  registry.registerEvent(C.orderPlacement, GenericEventCard);
  registry.registerEvent(C.orderRefund, GenericEventCard);
  registry.registerHints(COWSWAP_HINTS);
  return registry;
}

export { COWSWAP_HINTS, CowAppData, CowOrderUid, CowTradeFlags, cowExplorerName, cowOrderUid, cowOrderUrl, registerCowSwap };
//# sourceMappingURL=chunk-I7F37TKG.js.map
//# sourceMappingURL=chunk-I7F37TKG.js.map